import { useCallback, useEffect, useState } from "react";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";

import { api } from "../utils/api";

export type StripePlanType =
  | "device-otp-monthly"
  | "device-otp-yearly"
  | "all-in-one-monthly";

// Helper function to validate plan types at runtime
const isValidPlanType = (plan: string): plan is StripePlanType => {
  return [
    "device-otp-monthly",
    "device-otp-yearly",
    "all-in-one-monthly",
  ].includes(plan);
};

// Test Stripe API key (public)
const stripePromise = loadStripe(
  "pk_test_51RiHCXRjb7mnidaxXJ5eGQDujS4M3bBPPZUieaDvQi2chqgch6GBBRGqR6gyIbbD7Y3lZQXEvM4D9JL60gkc25tH00KMD3k4p7",
);

export default function StripeCheckout() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<StripePlanType | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get the selected plan from sessionStorage
  useEffect(() => {
    const planFromStorage = sessionStorage.getItem("selectedPlan");
    if (!planFromStorage) {
      // If no plan selected, redirect back to plan selection
      navigate("/stripe");
      return;
    }

    // Validate that the plan is a valid PlanType
    if (isValidPlanType(planFromStorage)) {
      setSelectedPlan(planFromStorage);
    } else {
      console.error("Invalid plan type from storage:", planFromStorage);
      navigate("/stripe");
    }
  }, [navigate]);

  // tRPC mutation to create checkout session
  const createCheckoutSession = (
    api as any
  ).stripe.createCheckoutSession.useMutation({
    onSuccess: (data: any) => {
      setClientSecret(data.clientSecret);
      setError(null);
    },
    onError: (error: any) => {
      console.error("Error creating checkout session:", error);
      setError("Failed to create checkout session. Please try again.");
    },
  });

  // Create checkout session when plan is available
  useEffect(() => {
    if (selectedPlan && !clientSecret && !createCheckoutSession.isPending) {
      createCheckoutSession.mutate({
        planType: selectedPlan,
      });
    }
  }, [selectedPlan, clientSecret, createCheckoutSession]);

  const fetchClientSecret = useCallback(() => {
    if (!clientSecret) {
      console.error("No client secret available");
    }
    return Promise.resolve(clientSecret);
  }, [clientSecret]);

  const options = { fetchClientSecret };

  return (
    <div className="min-w-screen w-screen min-h-screen h-full flex flex-col bg-surface-one">
      <div className="flex-1 flex flex-col items-center justify-center px-2 py-2">
        <div className="w-full max-w-4xl h-full flex flex-col grow bg-white rounded-2xl">
          <div className="grow mx-auto flex flex-col items-center justify-center mt-8 mb-8 px-4 gap-8">
            {/* Header Section */}
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-sm uppercase text-brand-primary">
                Secure Checkout
              </p>
              <p className="text-4xl text-foreground-primary">Complete Your</p>
              <p className="text-4xl font-bold text-foreground-primary">
                Purchase
              </p>
              <p className="text-lg text-foreground-secondary mt-4 max-w-2xl">
                {selectedPlan && selectedPlan.includes("device")
                  ? "Your Nettle device and app subscription in one simple checkout. Device will be shipped, and your subscription starts immediately."
                  : "Your information is secure and encrypted. Complete your purchase below."}
              </p>
            </div>

            {/* Loading State */}
            {createCheckoutSession.isPending && (
              <div className="w-full max-w-2xl flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
                <p className="text-lg text-foreground-secondary">
                  Setting up your checkout...
                </p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="w-full max-w-2xl">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                  <p className="text-red-600 mb-4">{error}</p>
                  <button
                    onClick={() => navigate("/stripe")}
                    className="px-6 py-2 bg-brand-primary text-white rounded-full hover:bg-brand-primary-darker transition-colors"
                  >
                    Back to Plans
                  </button>
                </div>
              </div>
            )}

            {/* Stripe Embedded Checkout */}
            {clientSecret && !error && (
              <div id="checkout" className="w-full max-w-2xl">
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={options}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
